import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import { supabase } from "../supabaseClient";

const MANAGE_TOKEN_KEY = "outfall_admin_session_token";
const MANAGE_TOKEN_META_KEY = "outfall_admin_session_meta";

const DEFAULT_TABS = [
  { tab_key: "hero", label: "Hero Section", enabled: true, sort_order: 1 },
  { tab_key: "stats", label: "Stats Boxes", enabled: true, sort_order: 2 },
  { tab_key: "news", label: "News Section", enabled: true, sort_order: 3 },
  { tab_key: "play", label: "Play Section", enabled: true, sort_order: 4 },
  { tab_key: "modes", label: "Modes Section", enabled: true, sort_order: 5 },

  { tab_key: "page_news", label: "News Page", enabled: true, sort_order: 20 },
  {
    tab_key: "page_tracker",
    label: "Tracker Page",
    enabled: true,
    sort_order: 30,
  },
  {
    tab_key: "page_careers",
    label: "Careers Page",
    enabled: true,
    sort_order: 40,
  },
  {
    tab_key: "page_status",
    label: "Status Page",
    enabled: true,
    sort_order: 50,
  },
  {
    tab_key: "page_ban_appeal",
    label: "Ban Appeal Page",
    enabled: true,
    sort_order: 60,
  },
  {
    tab_key: "page_my_application",
    label: "My Application Page",
    enabled: true,
    sort_order: 70,
  },
  {
    tab_key: "page_terms",
    label: "Terms Page",
    enabled: true,
    sort_order: 80,
  },
  {
    tab_key: "page_privacy",
    label: "Privacy Page",
    enabled: true,
    sort_order: 90,
  },
  {
    tab_key: "page_cookies",
    label: "Cookie Policy Page",
    enabled: true,
    sort_order: 100,
  },
];

function createLocalToken() {
  if (crypto?.randomUUID) return crypto.randomUUID();
  return `manage_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function getStoredAdminToken() {
  try {
    return localStorage.getItem(MANAGE_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStoredAdminToken(user) {
  try {
    const existingToken = localStorage.getItem(MANAGE_TOKEN_KEY);
    const token = existingToken || createLocalToken();

    localStorage.setItem(MANAGE_TOKEN_KEY, token);
    localStorage.setItem(
      MANAGE_TOKEN_META_KEY,
      JSON.stringify({
        discordId: user?.discordId || null,
        username: user?.username || null,
        createdAt: Date.now(),
      }),
    );

    return token;
  } catch {
    return null;
  }
}

function clearManageLocalSession() {
  try {
    localStorage.removeItem(MANAGE_TOKEN_KEY);
    localStorage.removeItem(MANAGE_TOKEN_META_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

function getCachedUser() {
  try {
    return JSON.parse(sessionStorage.getItem("manage_user_cache"));
  } catch {
    return null;
  }
}

function getCachedRole() {
  try {
    return JSON.parse(sessionStorage.getItem("manage_role_cache"));
  } catch {
    return null;
  }
}

export default function Manage() {
  const location = useLocation();
  const cachedUser = getCachedUser();
  const cachedRole = getCachedRole();

  const [loading, setLoading] = useState(!cachedUser);
  const [roleLoading, setRoleLoading] = useState(!cachedRole);
  const [user, setUser] = useState(cachedUser);
  const [allowed, setAllowed] = useState(Boolean(cachedRole?.allowed));
  const [error, setError] = useState("");

  useEffect(() => {
    checkSession({ silent: Boolean(cachedUser) });

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        sessionStorage.removeItem("manage_user_cache");
        sessionStorage.removeItem("manage_role_cache");
        clearManageLocalSession();

        setUser(null);
        setAllowed(false);
        setLoading(false);
        setRoleLoading(false);
      }

      if (event === "SIGNED_IN") {
        checkSession({ silent: false, forceRoleCheck: true });
      }
    });

    return () => listener?.subscription?.unsubscribe();
  }, []);

  async function checkSession({ silent = false, forceRoleCheck = false } = {}) {
    if (!silent) {
      setLoading(true);
      setRoleLoading(true);
    }

    setError("");

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const currentUser = session?.user;

    if (!currentUser) {
      sessionStorage.removeItem("manage_user_cache");
      sessionStorage.removeItem("manage_role_cache");
      clearManageLocalSession();

      setUser(null);
      setAllowed(false);
      setLoading(false);
      setRoleLoading(false);
      return;
    }

    const discordId =
      currentUser.user_metadata?.provider_id ||
      currentUser.user_metadata?.sub ||
      currentUser.identities?.[0]?.identity_data?.sub;

    const formattedUser = {
      ...currentUser,
      discordId,
      username:
        currentUser.user_metadata?.full_name ||
        currentUser.user_metadata?.name ||
        currentUser.user_metadata?.custom_claims?.global_name ||
        "Discord User",
      avatar: currentUser.user_metadata?.avatar_url,
    };

    setStoredAdminToken(formattedUser);
    sessionStorage.setItem("manage_user_cache", JSON.stringify(formattedUser));

    setUser(formattedUser);
    setLoading(false);

    const cachedRoleRaw = sessionStorage.getItem("manage_role_cache");

    if (cachedRoleRaw && !forceRoleCheck) {
      try {
        const parsed = JSON.parse(cachedRoleRaw);

        if (parsed.discordId === discordId) {
          setAllowed(Boolean(parsed.allowed));
          setRoleLoading(false);
          return;
        }
      } catch {
        sessionStorage.removeItem("manage_role_cache");
      }
    }

    try {
      const roleRes = await fetch("/api/check-staff-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-local-admin-session": getStoredAdminToken() || "",
        },
        body: JSON.stringify({
          discord_id: discordId,
          secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
        }),
      });

      const roleData = await roleRes.json();
      const hasRole = Boolean(roleData.hasRole);

      setAllowed(hasRole);

      sessionStorage.setItem(
        "manage_role_cache",
        JSON.stringify({
          discordId,
          allowed: hasRole,
          checkedAt: Date.now(),
        }),
      );
    } catch {
      setAllowed(false);
      setError("Could not verify your Discord staff role.");
    }

    setRoleLoading(false);
  }

  async function handleLogout() {
    sessionStorage.removeItem("manage_user_cache");
    sessionStorage.removeItem("manage_role_cache");
    clearManageLocalSession();

    await supabase.auth.signOut();

    setUser(null);
    setAllowed(false);
    setLoading(false);
    setRoleLoading(false);
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />

        <section className="flex min-h-screen items-center justify-center px-6">
          <p className="font-black uppercase tracking-[0.18em] text-white/40">
            Checking session...
          </p>
        </section>
      </main>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(
          location.pathname + location.search,
        )}`}
        replace
      />
    );
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#050505] text-white">
      <Header />

      <section className="relative px-6 pb-20 pt-36">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,217,120,0.18),transparent_35%),radial-gradient(circle_at_top_right,rgba(255,107,0,0.16),transparent_35%)]" />

        <div className="relative mx-auto max-w-7xl">
          <header className="mb-10 rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/80 p-8 shadow-[0_0_35px_rgba(255,143,107,0.12)] backdrop-blur">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="mb-3 font-black uppercase tracking-[0.25em] text-[#ffb56f]">
                  Site Management
                </p>

                <h1 className="outfall-heading text-[clamp(44px,7vw,92px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
                  Manage Tabs
                </h1>

                <p className="mt-5 max-w-3xl text-lg font-semibold leading-relaxed text-white/60">
                  Control which public pages and homepage tabs are visible. Home
                  can never be disabled.
                </p>
              </div>

              <div className="inline-flex w-fit items-center gap-3 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-5 py-3 font-black uppercase text-emerald-300 shadow-[0_0_18px_rgba(16,185,129,0.15)]">
                <span className="h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_14px_#34d399]" />
                Discord Role Protected
              </div>
            </div>
          </header>

          {roleLoading && (
            <section className="mx-auto max-w-2xl rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/90 p-8 text-center shadow-[0_0_35px_rgba(255,217,120,0.08)]">
              <p className="font-black uppercase tracking-[0.18em] text-white/40">
                Checking Discord staff role...
              </p>
            </section>
          )}

          {!roleLoading && !allowed && (
            <AccessDenied user={user} error={error} onLogout={handleLogout} />
          )}

          {!roleLoading && allowed && (
            <>
              <SignedInBar user={user} onLogout={handleLogout} />
              <ManagePanel />
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function AccessDenied({ user, error, onLogout }) {
  return (
    <section className="mx-auto max-w-2xl rounded-[2rem] border border-red-400/25 bg-red-950/20 p-8 shadow-[0_0_35px_rgba(248,113,113,0.08)]">
      <h2 className="text-3xl font-black text-red-300">Access Denied</h2>

      <p className="mt-3 font-semibold text-white/55">
        Your Discord account is signed in, but you do not have one of the
        required admin roles.
      </p>

      {error && (
        <p className="mt-5 rounded-xl bg-red-500/20 px-4 py-3 text-sm font-bold text-red-300">
          {error}
        </p>
      )}

      <div className="mt-6 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
        {user.avatar && (
          <img
            src={user.avatar}
            alt=""
            className="h-12 w-12 rounded-full border border-white/10"
          />
        )}

        <div>
          <p className="font-black text-white">{user.username}</p>
          <p className="font-mono text-sm text-white/35">{user.discordId}</p>
        </div>
      </div>

      <button
        onClick={onLogout}
        type="button"
        className="mt-6 rounded-2xl border border-red-300/30 bg-red-400/10 px-6 py-3 font-black uppercase text-red-300 transition hover:bg-red-400/20"
      >
        Sign Out
      </button>
    </section>
  );
}

function SignedInBar({ user, onLogout }) {
  return (
    <div className="mb-6 flex flex-col gap-4 rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/80 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        {user.avatar && (
          <img
            src={user.avatar}
            alt=""
            className="h-12 w-12 rounded-full border border-[#ffd978]/30"
          />
        )}

        <div>
          <p className="font-black text-[#ffd978]">
            Signed in as {user.username}
          </p>
          <p className="font-mono text-sm text-white/35">{user.discordId}</p>
        </div>
      </div>

      <button
        onClick={onLogout}
        type="button"
        className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 font-black uppercase text-white/60 transition hover:border-red-400/30 hover:bg-red-400/10 hover:text-red-300"
      >
        Sign Out
      </button>
    </div>
  );
}

function ManagePanel() {
  const [tabs, setTabs] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const pageTabs = tabs.filter((tab) => tab.tab_key.startsWith("page_"));
  const sectionTabs = tabs.filter((tab) => !tab.tab_key.startsWith("page_"));

  async function seedDefaultTabs() {
    const { error: seedError } = await supabase
      .from("Tabs")
      .upsert(DEFAULT_TABS, { onConflict: "tab_key" });

    if (seedError) throw seedError;
  }

  async function loadTabs() {
    setLoadingTabs(true);
    setMessage("");
    setError("");

    try {
      let { data, error: loadError } = await supabase
        .from("Tabs")
        .select("id, tab_key, label, enabled, sort_order, created_at")
        .order("sort_order", { ascending: true })
        .order("label", { ascending: true });

      if (loadError) throw loadError;

      if (!data || data.length <= 0) {
        await seedDefaultTabs();

        const retry = await supabase
          .from("Tabs")
          .select("id, tab_key, label, enabled, sort_order, created_at")
          .order("sort_order", { ascending: true })
          .order("label", { ascending: true });

        if (retry.error) throw retry.error;

        data = retry.data || [];
        setMessage("Default tabs created successfully.");
      }

      setTabs(data || []);
    } catch (err) {
      setError(err.message || "Could not load tabs.");
    }

    setLoadingTabs(false);
  }

  async function toggleTab(tab) {
    setSaving(true);
    setMessage("");
    setError("");

    const nextEnabled = !tab.enabled;

    setTabs((prev) =>
      prev.map((item) =>
        item.tab_key === tab.tab_key
          ? {
              ...item,
              enabled: nextEnabled,
            }
          : item,
      ),
    );

    try {
      const { error: updateError } = await supabase
        .from("Tabs")
        .update({
          enabled: nextEnabled,
        })
        .eq("tab_key", tab.tab_key);

      if (updateError) throw updateError;

      setMessage(
        `${tab.label} has been ${nextEnabled ? "enabled" : "disabled"}.`,
      );
    } catch (err) {
      setTabs((prev) =>
        prev.map((item) =>
          item.tab_key === tab.tab_key
            ? {
                ...item,
                enabled: tab.enabled,
              }
            : item,
        ),
      );

      setError(err.message || "Could not update tab.");
    }

    setSaving(false);
  }

  async function resetTabs() {
    const confirmReset = window.confirm(
      "Are you sure you want to reset the Tabs table to defaults?",
    );

    if (!confirmReset) return;

    setSaving(true);
    setMessage("");
    setError("");

    try {
      await seedDefaultTabs();
      await loadTabs();
      setMessage("Tabs reset successfully.");
    } catch (err) {
      setError(err.message || "Could not reset tabs.");
    }

    setSaving(false);
  }

  function TabCard({ tab }) {
    return (
      <article className="rounded-3xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-[#ffd978]/25 hover:bg-white/[0.06]">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-2xl font-black text-white">{tab.label}</h3>

              <span
                className={`rounded-full px-3 py-1 text-xs font-black uppercase ${
                  tab.enabled
                    ? "border border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                    : "border border-red-400/30 bg-red-400/10 text-red-300"
                }`}
              >
                {tab.enabled ? "Enabled" : "Disabled"}
              </span>
            </div>

            <p className="mt-2 font-mono text-sm text-white/35">
              {tab.tab_key}
            </p>

            <p className="mt-1 text-sm font-semibold text-white/30">
              Sort Order: {tab.sort_order ?? 0}
            </p>
          </div>

          <button
            onClick={() => toggleTab(tab)}
            disabled={saving}
            type="button"
            className="group flex cursor-pointer items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-[#ffd978]/30 hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="font-black uppercase text-white/60">
              {tab.enabled ? "On" : "Off"}
            </span>

            <span
              className={`relative flex h-7 w-12 shrink-0 items-center rounded-full border transition ${
                tab.enabled
                  ? "border-[#ffd978]/60 bg-gradient-to-r from-[#ffd978] to-[#ff6b00]"
                  : "border-white/10 bg-white/10"
              }`}
            >
              <span
                className={`absolute left-1 h-5 w-5 rounded-full bg-white shadow-lg transition ${
                  tab.enabled ? "translate-x-5" : ""
                }`}
              />
            </span>
          </button>
        </div>
      </article>
    );
  }

  useEffect(() => {
    loadTabs();
  }, []);

  return (
    <section className="rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/90 p-7 shadow-[0_0_35px_rgba(255,217,120,0.08)]">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-3xl font-black text-[#ffd978]">Site Controls</h2>
          <p className="mt-1 font-semibold text-white/45">
            Toggle public pages and homepage tabs on or off.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={loadTabs}
            disabled={loadingTabs || saving}
            type="button"
            className="rounded-2xl border border-[#ffd978]/25 bg-[#ffd978]/10 px-5 py-3 font-black uppercase text-[#ffd978] transition hover:bg-[#ffd978]/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingTabs ? "Loading..." : "Refresh"}
          </button>

          <button
            onClick={resetTabs}
            disabled={loadingTabs || saving}
            type="button"
            className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 font-black uppercase text-white/60 transition hover:border-[#ffd978]/25 hover:bg-[#ffd978]/10 hover:text-[#ffd978] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset Defaults
          </button>
        </div>
      </div>

      {message && (
        <p className="mb-5 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm font-black text-emerald-300">
          {message}
        </p>
      )}

      {error && (
        <p className="mb-5 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm font-black text-red-300">
          {error}
        </p>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        <section className="rounded-[1.75rem] border border-white/10 bg-black/25 p-5">
          <div className="mb-5">
            <p className="font-black uppercase tracking-[0.2em] text-[#ffb56f]">
              Pages
            </p>

            <h3 className="mt-2 text-4xl font-black text-[#ffd978]">Pages</h3>

            <p className="mt-2 font-semibold text-white/40">
              Controls full public routes like News, Tracker, Careers, Status,
              and appeal pages.
            </p>
          </div>

          <div className="grid gap-4">
            {pageTabs.length > 0 ? (
              pageTabs.map((tab) => <TabCard key={tab.tab_key} tab={tab} />)
            ) : (
              <p className="rounded-3xl border border-dashed border-white/10 bg-white/[0.03] p-8 text-center font-black uppercase tracking-[0.18em] text-white/30">
                No pages loaded.
              </p>
            )}
          </div>
        </section>

        <section className="rounded-[1.75rem] border border-white/10 bg-black/25 p-5">
          <div className="mb-5">
            <p className="font-black uppercase tracking-[0.2em] text-[#ffb56f]">
              Tabs
            </p>

            <h3 className="mt-2 text-4xl font-black text-[#ffd978]">Tabs</h3>

            <p className="mt-2 font-semibold text-white/40">
              Controls homepage sections and header anchor links.
            </p>
          </div>

          <div className="grid gap-4">
            {sectionTabs.length > 0 ? (
              sectionTabs.map((tab) => <TabCard key={tab.tab_key} tab={tab} />)
            ) : (
              <p className="rounded-3xl border border-dashed border-white/10 bg-white/[0.03] p-8 text-center font-black uppercase tracking-[0.18em] text-white/30">
                No tabs loaded.
              </p>
            )}
          </div>
        </section>
      </div>
    </section>
  );
}
